import * as React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import user from 'js/lib/user';

import withSessionsV2EnrollmentEnabled from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import StartDateString from 'bundles/enroll-course/components/StartDateString';
import EnrollModal from 'bundles/enroll/components/EnrollModal';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import type { EnrollmentChoices } from 'bundles/enroll/components/xdp/withEnrollment';
import { ENROLL } from 'bundles/enroll/utils/enrollActionParams';
import Icon from 'bundles/iconfont/Icon';
import Naptime from 'bundles/naptimejs';
import CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import uriManager from 'bundles/page/lib/uriManager';
import TopLevelModal from 'bundles/phoenix/components/TopLevelModal';

import _t from 'i18n!nls/enroll-course';

import 'css!./__styles__/CourseEnrollButton';

type PropsFromCaller = {
  courseId: string;
  extraClassName?: string;
  showStartDate?: boolean;
  enrollButtonText?: string;
};

type PropsToComponent = PropsFromCaller & {
  course?: CoursesV1;
  enrollmentAvailableChoices?: EnrollmentChoices;
  isDashboard?: boolean; // TODO (Billy) revisit this after experiment
  onClick?: () => void;
  buttonClickTrackingName?: string;
  buttonTextClassName?: string;
  buttonElementNameOverride?: string;
  hideIfEnrolled?: boolean;
  data: Record<string, string | number>;
  s12nId?: string;
  enrollButtonScreenReaderText: React.ReactNode | null;
  sessionsV2EnrollmentEnabled: boolean;
};

type State = {
  showCourseEnrollModal: boolean;
  didMount: boolean;
};

class CourseEnrollButtonWithData extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof CourseEnrollButtonWithData.contextTypes>;

  static defaultProps = {
    showStartDate: true,
    buttonClickTrackingName: 'enroll_button',
    buttonTextClassName: 'headline-1-text enroll-text',
  };

  state: State = {
    showCourseEnrollModal: false,
    didMount: false,
  };

  componentDidMount() {
    const action = uriManager.parseAndDeleteQueryParam('action');
    const isAuthenticated = user.isAuthenticatedUser();

    const showCourseEnrollModal = this.isEnrollable() && isAuthenticated && action === ENROLL;
    this.setState(() => ({ didMount: true, showCourseEnrollModal }));
  }

  handleModelClose = (): void => {
    this.setState(() => ({ showCourseEnrollModal: false }));
  };

  onClick(ev: $TSFixMe) {
    ev.preventDefault();
    const { router } = this.context;
    const isAuthenticated = user.isAuthenticatedUser();

    if (isAuthenticated) {
      this.setState(() => ({ showCourseEnrollModal: true }));
    } else {
      router.replace({
        pathname: router.location.pathname,
        query: { ...router.location.query, authMode: 'signup', action: ENROLL },
      });
    }

    if (typeof this.props.onClick === 'function') {
      this.props.onClick();
    }
  }

  isEnrolled() {
    const { enrollmentAvailableChoices } = this.props;
    return enrollmentAvailableChoices && enrollmentAvailableChoices.isEnrolled;
  }

  isCapstoneAccessLocked() {
    const { enrollmentAvailableChoices } = this.props;
    return enrollmentAvailableChoices && enrollmentAvailableChoices.isCapstoneAccessLocked;
  }

  isPreEnrolled() {
    const { course } = this.props;
    return this.isEnrolled() && course?.isPreEnroll;
  }

  isLinkToCourseHome() {
    return this.isEnrolled() && !this.isCapstoneAccessLocked() && !this.isPreEnrolled();
  }

  isEnrollable() {
    return !this.isEnrolled();
  }

  render() {
    const {
      extraClassName,
      courseId,
      course,
      showStartDate,
      isDashboard,
      enrollButtonText = _t('Enroll'),
      buttonClickTrackingName,
      buttonTextClassName,
      buttonElementNameOverride,
      data,
      s12nId,
      hideIfEnrolled,
      enrollButtonScreenReaderText,
      sessionsV2EnrollmentEnabled,
    } = this.props;
    const { showCourseEnrollModal, didMount } = this.state;

    const elClassName = buttonElementNameOverride || 'rc-CourseEnrollButton';
    const buttonClassName = classNames('course-enroll-button-wrapper', extraClassName);

    const isEnrolled = this.isEnrolled();
    const isLinkToCourseHome = this.isLinkToCourseHome();
    const isEnrollable = this.isEnrollable();

    const shouldShowStartDate = !isDashboard && (sessionsV2EnrollmentEnabled || showStartDate);

    const renderLoadingButton = () => (
      <div className={classNames(elClassName, 'is-loading')}>
        <button type="button" className={buttonClassName} disabled={true}>
          <Icon name="spinner" className="cif-spin" />
        </button>
      </div>
    );

    if (isEnrolled) {
      // For enrolled courses that launched, display secondary button to dashboard
      // Users with group memberships or private session get link to course home instead
      if (hideIfEnrolled) {
        return null;
      } else if (isLinkToCourseHome) {
        return (
          <div className={elClassName}>
            <TrackedA
              href={course?.learnerPhoenixHomeLink}
              className={classNames(buttonClassName, 'link-button secondary')}
              trackingName="go_to_course_button"
              aria-label={_t('Go to #{courseName} course', { courseName: course?.name })}
            >
              <span className={buttonTextClassName}>{_t('Go to Course')}</span>
            </TrackedA>
          </div>
        );
      } else {
        // For enrolled courses that are pre-enroll or locked, display disabled button with start date (if relevant)
        return (
          <div className={classNames(elClassName, 'is-enrolled')}>
            <button type="button" className={classNames(buttonClassName, 'primary')} disabled={true}>
              <span className={buttonTextClassName}>
                {_t('Enrolled')}
                {this.isPreEnrolled() && <StartDateString courseId={courseId} />}
              </span>
            </button>
          </div>
        );
      }
    } else if (isEnrollable) {
      // Display enroll button with click handler
      return (
        <div className={elClassName}>
          <TrackedButton
            className={classNames(buttonClassName, 'primary')}
            trackingName={buttonClickTrackingName}
            data={{ id: courseId, ...data }}
            onClick={(ev) => this.onClick(ev)}
            disabled={!didMount}
            id="course-enroll-button"
            aria-label={_t('Enroll in #{courseName} course', { courseName: course?.name })}
            aria-labelledby="course-enroll-button start-date-string"
            data-e2e="enroll-button"
          >
            {enrollButtonScreenReaderText}
            <span className={buttonTextClassName}>{enrollButtonText}</span>
            {shouldShowStartDate && <StartDateString courseId={courseId} />}
          </TrackedButton>
          {showCourseEnrollModal && (
            <TopLevelModal>
              <EnrollModal
                courseId={courseId}
                s12nId={s12nId}
                onClose={() => this.setState({ showCourseEnrollModal: false })}
              />
            </TopLevelModal>
          )}
        </div>
      );
    } else {
      return renderLoadingButton();
    }
  }
}

type PropsFromNaptime1 = PropsFromCaller & {
  course: CoursesV1;
};

export default compose<PropsToComponent, PropsFromCaller>(
  withSessionsV2EnrollmentEnabled(({ courseId }) => courseId),
  Naptime.createContainer<PropsFromNaptime1, PropsFromCaller>(({ courseId }: { courseId: string }) => ({
    course: CoursesV1.get(courseId, {
      fields: ['name', 'metadata', 's12nIds'],
    }),
  })),
  withEnrollment()
)(CourseEnrollButtonWithData);
